import React from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SwipeCard from "./SwipeCard";
import ActionBar from "./ActionBar";
import { ProgressPill, PrimaryButton } from "./bits";
import { useApp } from "../context/AppContext";
import { endpoints } from "../lib/api";
import * as store from "../lib/storage";
import { burst, doubleBurst, haptic } from "../lib/confetti";
import { SUPPORT_ENABLED } from "../lib/features";
import { UBot } from "./UBot";

const MILESTONES = [10, 25, 50, 99];

export default function Deck({ projects, total, onOpenSupport, hasFilters, onClearFilters, onReshuffle }) {
  const navigate = useNavigate();
  const { toggleSave, isSaved } = useApp();
  const [index, setIndex] = React.useState(0);
  const [history, setHistory] = React.useState([]);
  const [, force] = React.useState(0);
  const directionRef = React.useRef(null);
  const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const sessionId = store.getSessionShuffle();

  React.useEffect(() => { setIndex(0); setHistory([]); }, [projects]);

  const seenCount = store.getSeen().length;
  const current = projects[index];

  const logEvent = (project, direction) => {
    endpoints.event({ project_id: project.id, session_id: sessionId, direction }).catch(() => {});
  };

  const celebrate = () => {
    const now = store.getSeen().length;
    if (MILESTONES.includes(now)) {
      doubleBurst();
      toast(`${now} projects explored! Keep going 🎉`);
    }
  };

  const advance = (dir) => {
    if (!current) return;
    directionRef.current = dir;
    logEvent(current, dir);
    store.addSeen(current.id);
    setHistory((h) => [...h, { id: current.id, dir }]);
    setIndex((i) => i + 1);
    setTimeout(celebrate, 50);
  };

  const onCommit = (dir) => {
    const proj = current;
    if (dir === "left") {
      advance("left");
      toast("Skipped. Changed your mind?", { action: { label: "Undo", onClick: onUndo } });
    } else if (dir === "up") {
      advance("up");
      setTimeout(() => navigate(`/p/${proj.slug}`), 120);
    } else if (dir === "right") {
      if (!reduced) {
        burst({ x: 0.75, y: 0.5 });
        haptic([12, 40, 18]);
      }
      if (SUPPORT_ENABLED) {
        advance("right");
        setTimeout(() => onOpenSupport(proj), 120);
      } else {
        if (!isSaved(proj.slug)) {
          toggleSave(proj);
          logEvent(proj, "save");
          toast("Saved ⭐ Find it anytime in Saved.");
        } else {
          toast("Already in your Saved ⭐");
        }
        advance("right");
      }
    }
  };

  const onUndo = () => {
    if (!history.length) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    const seen = new Set(store.getSeen());
    seen.delete(last.id);
    localStorage.setItem("uhack_seen", JSON.stringify([...seen]));
    directionRef.current = null;
    setIndex((i) => Math.max(0, i - 1));
    logEvent({ id: last.id }, "undo");
    toast("Back again!");
  };

  const onSave = () => {
    if (!current) return;
    const nowSaved = toggleSave(current);
    logEvent(current, "save");
    if (nowSaved) { if (!reduced) burst({ x: 0.9, y: 0.85, count: 24, startVelocity: 26 }); toast("Saved ⭐ Find it anytime in Saved."); }
    else toast("Removed from Saved");
    force((n) => n + 1);
  };

  // keyboard
  React.useEffect(() => {
    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (!current) return;
      if (e.key === "ArrowLeft") onCommit("left");
      else if (e.key === "ArrowRight") onCommit("right");
      else if (e.key === "ArrowUp" || e.key === "Enter") onCommit("up");
      else if (e.key === "z" || e.key === "Z" || e.key === "Backspace") onUndo();
      else if (e.key === "s" || e.key === "S") onSave();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }); // eslint-disable-line

  // empty states
  if (total === 0) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-16 text-center" data-testid="deck-nofilter">
        <UBot size={96} />
        <h2 className="font-display text-[28px] font-bold">No matches… yet</h2>
        <p className="max-w-xs text-on-dark-muted">U-Bot searched everywhere and found no projects. Try removing a filter.</p>
        {hasFilters && <PrimaryButton onClick={onClearFilters} data-testid="clear-filters-empty">Clear filters</PrimaryButton>}
      </div>
    );
  }

  if (!current) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-16 text-center" data-testid="deck-empty">
        <div className="animate-ubot-bounce"><UBot size={110} waving /></div>
        <h2 className="font-display text-[28px] font-bold">You've met every builder! 🎉</h2>
        <p className="max-w-xs text-on-dark-muted">That's all {total} projects. Want another look at the ones you skipped?</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <PrimaryButton onClick={onReshuffle} data-testid="reshuffle-btn">Reshuffle skipped</PrimaryButton>
          <button onClick={() => navigate("/explore")} className="rounded-pill border border-white/20 px-5 py-3 font-semibold text-white">Browse all</button>
          <button onClick={() => navigate("/saved")} className="rounded-pill px-5 py-3 font-semibold text-violet-300">See my saved</button>
        </div>
      </div>
    );
  }

  const stack = projects.slice(index, index + 3);

  return (
    <div className="flex w-full flex-col items-center" data-testid="deck">
      <div className="mb-3 flex w-full max-w-[420px] items-center justify-between px-1">
        <ProgressPill seen={Math.min(seenCount, total)} total={total} />
        <span className="hidden text-[12px] text-on-dark-muted sm:block">← skip · ↑ more · {SUPPORT_ENABLED ? "→ support" : "→ save"} · Z undo{SUPPORT_ENABLED ? " · S save" : ""}</span>
      </div>

      <div className="relative w-full max-w-[420px]" style={{ aspectRatio: "3 / 4.6" }}>
        <AnimatePresence custom={directionRef.current}>
          {stack.map((p, i) => (
            <SwipeCard
              key={p.id}
              project={p}
              isTop={i === 0}
              offsetIndex={i}
              onCommit={onCommit}
              showHint={i === 0 && index < 3}
              reduced={reduced}
              directionRef={directionRef}
            />
          )).reverse()}
        </AnimatePresence>
      </div>

      <div className="mt-6">
        <ActionBar
          onUndo={onUndo}
          onSkip={() => onCommit("left")}
          onLearn={() => onCommit("up")}
          onSupport={() => onCommit("right")}
          onSave={onSave}
          canUndo={history.length > 0}
          saved={current ? isSaved(current.slug) : false}
          supportEnabled={SUPPORT_ENABLED}
        />
      </div>
    </div>
  );
}
